// @ts-nocheck
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Upload, Trash2, Check } from "lucide-react";

function SportCoverCard({ sport, onChange }: { sport: any; onChange: () => void }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUpload = async (file: File) => {
    setError(null);
    setUploading(true);
    try {
      if (!file.type.startsWith("image/")) {
        throw new Error("Please upload an image file (JPG or PNG).");
      }
      if (file.size > 5 * 1024 * 1024) {
        throw new Error("Image must be under 5MB.");
      }

      const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
      const path = `sport-covers/${sport.id}-${Date.now()}.${ext}`;

      const { error: uploadErr } = await (supabase as any).storage
        .from("event-assets")
        .upload(path, file, { upsert: true, cacheControl: "3600" });
      if (uploadErr) throw uploadErr;

      const { data: pub } = (supabase as any).storage
        .from("event-assets")
        .getPublicUrl(path);
      const publicUrl = pub?.publicUrl;
      if (!publicUrl) throw new Error("Could not get public URL.");

      const { data: updatedSport, error: updateErr } = await (supabase as any)
        .from("sports")
        .update({ default_cover_photo_url: publicUrl })
        .eq("id", sport.id)
        .select("id, default_cover_photo_url")
        .maybeSingle();
      if (updateErr) throw updateErr;
      if (!updatedSport?.default_cover_photo_url) {
        throw new Error("Upload succeeded, but this account is not allowed to update the sport cover photo.");
      }

      onChange();
    } catch (e: any) {
      setError(e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!confirm(`Remove the cover photo for ${sport.name}?`)) return;
    setUploading(true);
    setError(null);
    try {
      const { data: updatedSport, error: updateErr } = await (supabase as any)
        .from("sports")
        .update({ default_cover_photo_url: null })
        .eq("id", sport.id)
        .select("id")
        .maybeSingle();
      if (updateErr) throw updateErr;
      if (!updatedSport) {
        throw new Error("This account is not allowed to remove the sport cover photo.");
      }
      onChange();
    } catch (e: any) {
      setError(e.message || "Remove failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-surface-card border border-stroke-subtle rounded-token-xl overflow-hidden">
      {/* Image preview / placeholder */}
      <div className="relative aspect-[16/9] bg-surface-sunken">
        {sport.default_cover_photo_url ? (
          <img
            src={sport.default_cover_photo_url}
            alt={`${sport.name} default cover`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-caption text-ink-tertiary uppercase tracking-wide">No default set</span>
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <Loader2 className="h-6 w-6 text-white animate-spin" />
          </div>
        )}
      </div>

      {/* Sport name + actions */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-heading-4 text-ink-primary">{sport.name}</h3>
          {sport.default_cover_photo_url && (
            <span className="inline-flex items-center gap-1 text-caption text-status-success">
              <Check className="h-3.5 w-3.5" /> Set
            </span>
          )}
        </div>

        {error && (
          <div className="text-caption text-status-danger mb-2">{error}</div>
        )}

        <div className="flex gap-2">
          <label className="flex-1 inline-flex items-center justify-center gap-2 bg-cyan text-cyan-foreground hover:bg-cyan-hover px-4 py-2 rounded-token-md text-body-sm font-medium cursor-pointer transition-colors">
            <Upload className="h-4 w-4" />
            {sport.default_cover_photo_url ? "Replace" : "Upload"}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              disabled={uploading}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleUpload(f);
                e.currentTarget.value = "";
              }}
            />
          </label>
          {sport.default_cover_photo_url && (
            <button
              type="button"
              onClick={handleRemove}
              disabled={uploading}
              className="inline-flex items-center justify-center bg-surface-page border border-stroke-subtle hover:border-status-danger hover:text-status-danger px-3 py-2 rounded-token-md text-body-sm transition-colors"
              title="Remove default cover"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SportCoverPhotosPage() {
  const { data: sports = [], isLoading, refetch } = useQuery({
    queryKey: ["admin-sport-cover-photos"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("sports")
        .select("id, name, default_cover_photo_url, is_active")
        .order("name");
      if (error) throw error;
      return data || [];
    },
  });

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-display-sm text-ink-primary font-display">Sport Cover Photos</h1>
        <p className="text-body-md text-ink-secondary mt-2 max-w-2xl">
          Upload a default cover photo for each sport. These are used as the
          hero banner on public event pages when an event doesn't have its
          own cover photo. Recommended: 1600×600 wide format, JPG or PNG.
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-ink-tertiary" />
        </div>
      ) : sports.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-body-md text-ink-tertiary">No sports found.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sports.map((sport: any) => (
            <SportCoverCard key={sport.id} sport={sport} onChange={refetch} />
          ))}
        </div>
      )}
    </div>
  );
}
